import React, { useState } from 'react';
import { View, Text, StyleSheet, ScrollView, Image, TouchableOpacity, Pressable, Platform } from 'react-native';
import { FontAwesome6 } from '@expo/vector-icons';
import { colors, typography } from '../../styles/theme';
import BaseModal, { modalStyles, HoverButton } from './BaseModal';
import ModalStickyFooter from '../ui/ModalStickyFooter';
import * as PlatformStorage from '../../utils/PlatformStorage';
import { Entitlements, PuzzleCache } from '../../types';

interface PackBrowserModalProps {
  visible: boolean;
  onClose: () => void;
  onBack: () => void;
  onSelectPack: (packId: string, packTitle: string) => void;
  onCreateCustom: () => void;
  entitlements: Entitlements;
  puzzleCache: PuzzleCache;
}

export interface PackData {
  id: string;
  title: string;
  description: string;
  priceLabel: string;
  imageUri: string;
  showChevron?: boolean;
  isFeatured?: boolean;
  priceId?: string;
}

const R2_URL = process.env.EXPO_PUBLIC_BUCKET_URL;

export const mockPacks: PackData[] = [
  {
    id: 'animals',
    title: 'Animals',
    description: 'A diverse collection of wildlife from around the world.',
    priceLabel: 'Free',
    imageUri: `${R2_URL}/animals/animals-1_thumb.webp`,
    showChevron: true,
  },
  {
    id: 'animals 2',
    title: 'Animals II',
    description: 'More stunning wildlife and creatures.',
    priceLabel: '$3',
    imageUri: `${R2_URL}/animals 2/animals 2-1_thumb.webp`,
    showChevron: true,
    priceId: 'pri_01ks79gs6849zjsfc3zneznj8d',
  },
  {
    id: 'ambiance',
    title: 'Ambiance',
    description: 'Mood-driven scenes, cozy lighting, and atmospheric vibes.',
    priceLabel: '$3',
    imageUri: `${R2_URL}/ambiance/ambiance-1_thumb.webp`,
    showChevron: true,
    priceId: 'pri_01ks79f41mcbw12ggc2yweg5pe',
  },

  {
    id: 'aquatic',
    title: 'Aquatic & Beach',
    description: 'Dive deep into oceanic biomes and marine life.',
    priceLabel: '$3',
    imageUri: `${R2_URL}/aquatic/aquatic-1_thumb.webp`,
    showChevron: true,
    priceId: 'pri_01ks79hjywdbwz8964xswb707r',
  },
  {
    id: 'birds',
    title: 'Birds',
    description: 'The iridescent beauty of avian life.',
    priceLabel: '$3',
    imageUri: `${R2_URL}/birds/birds-3_thumb.webp`,
    showChevron: true,
    priceId: 'pri_01ks79jbp10nfrjc3atr7fr2q3',
  },
  {
    id: 'butterflies',
    title: 'Butterflies',
    description: 'Delicate wings and vibrant geometric patterns.',
    priceLabel: '$3',
    imageUri: `${R2_URL}/butterflies/butterflies-1_thumb.webp`,
    showChevron: true,
    priceId: 'pri_01ks79kex8n0cex25vf10emfp9',
  },
  {
    id: 'cats',
    title: 'Cats',
    description: 'Feline shapes and mysterious contours.',
    priceLabel: '$3',
    imageUri: `${R2_URL}/cats/cats-1_thumb.webp`,
    showChevron: true,
    priceId: 'pri_01ks79n58bd1403j68rpcmmh4t',
  },
  {
    id: 'cats 2',
    title: 'Cats II',
    description: 'More fluffy terrorclaws and cat dimensions.',
    priceLabel: '$3',
    imageUri: `${R2_URL}/cats 2/cats 2-1_thumb.webp`,
    showChevron: true,
    priceId: 'pri_01ks79nstzcf713gxe0rgbzfvc',
  },
  {
    id: 'dogs',
    title: 'Dogs',
    description: 'Man\'s best friend in geometric form.',
    priceLabel: '$3',
    imageUri: `${R2_URL}/dogs/dogs-1_thumb.webp`,
    showChevron: true,
    priceId: 'pri_01ks79pd1z6kf2n9zj2nngzzf6',
  },
  {
    id: 'dogs 2',
    title: 'Dogs II',
    description: 'More playful pups and loyal companions.',
    priceLabel: '$3',
    imageUri: `${R2_URL}/dogs 2/dogs 2-1_thumb.webp`,
    showChevron: true,
    priceId: 'pri_01ks79pxhjazy8q190rb04q9kw',
  },
  {
    id: 'flowers',
    title: 'Botanical',
    description: 'Vibrant blooms and intricate floral patterns.',
    priceLabel: '$3',
    imageUri: `${R2_URL}/flowers/flowers-1_thumb.webp`,
    showChevron: true,
    priceId: 'pri_01ks79qctmn9fze0xrb84dmcee',
  },
  {
    id: 'flowers 2',
    title: 'Botanical II',
    description: 'A second bouquet of beautiful floral scenes.',
    priceLabel: '$3',
    imageUri: `${R2_URL}/flowers 2/flowers 2-1_thumb.webp`,
    showChevron: true,
    priceId: 'pri_01ks79r1gr1jj9qhwczfcb0k84',
  },
  {
    id: 'holiday',
    title: 'Holiday',
    description: 'Festive cheer and seasonal celebrations.',
    priceLabel: '$3',
    imageUri: `${R2_URL}/holiday/holiday-1_thumb.webp`,
    showChevron: true,
    priceId: 'pri_01ks79rme3tyype0p9ezze79nb',
  },
  {
    id: 'landscapes',
    title: 'Landscapes',
    description: 'Breathtaking vistas and natural horizons.',
    priceLabel: '$3',
    imageUri: `${R2_URL}/landscapes/landscapes-1_thumb.webp`,
    showChevron: true,
    priceId: 'pri_01ks79s2nvrcd94qbgc92hjevz',
  },
  {
    id: 'landscapes 2',
    title: 'Landscapes II',
    description: 'More serene outdoor environments and vistas.',
    priceLabel: '$3',
    imageUri: `${R2_URL}/landscapes 2/landscapes 2-21_thumb.webp`,
    showChevron: true,
    priceId: 'pri_01ks79shax8340w0haz6wd5kdf',
  },
  {
    id: 'retro',
    title: 'Retro',
    description: 'Nostalgic scenes and cozy vintage palettes.',
    priceLabel: '$3',
    imageUri: `${R2_URL}/retro/retro-1_thumb.webp`,
    showChevron: true,
    priceId: 'pri_01ks79t2jhdemk4gm3mp84sbxj',
  },
];

export default function PackBrowserModal({ visible, onClose, onBack, onSelectPack, onCreateCustom, entitlements }: PackBrowserModalProps) {
  const [hasDismissedCustomBanner, setHasDismissedCustomBanner] = useState(false);

  React.useEffect(() => {
    const checkDismissed = async () => {
      const dismissed = await PlatformStorage.getItem('hasDismissedCustomBanner');
      if (dismissed === 'true') {
        setHasDismissedCustomBanner(true);
      }
    };
    checkDismissed();
  }, []);

  const handleDismissBanner = async () => {
    setHasDismissedCustomBanner(true);
    await PlatformStorage.setItem('hasDismissedCustomBanner', 'true');
  };

  return (
    <BaseModal
      visible={visible}
      onClose={onClose}
      header={(
        <>
          <View style={styles.headerRow}>
            <TouchableOpacity onPress={onBack} style={styles.backButton}>
              <FontAwesome6 name="arrow-left" size={20} color={colors.text} solid />
            </TouchableOpacity>
            <Text style={[modalStyles.headerText, { flex: 1, marginBottom: 0 }]}>Puzzle Packs</Text>
            <View style={{ width: 44 }} />
          </View>
          <Text style={styles.subtitleText}>Pick a pack to unlock new puzzles.</Text>
        </>
      )}
      footer={(
        <ModalStickyFooter>
          <HoverButton
            style={modalStyles.secondaryButton}
            textStyle={modalStyles.secondaryButtonText}
            onPress={onClose}
          >
            Close
          </HoverButton>
        </ModalStickyFooter>
      )}
    >

      {!entitlements.picloPlay ? (
        <View>
          {/* The Premium Piclo Play Banner */}
          <Pressable
            style={({ pressed }) => [
              styles.premiumBanner,
              pressed && styles.premiumBannerPressed
            ]}
            onPress={() => {
              if (Platform.OS === 'web') {
                if ((window as any).Paddle?.Checkout) {
                  (window as any).Paddle.Checkout.open({ items: [{ priceId: 'pri_01ks79bqva52p3cbwd6m18ajn0', quantity: 1 }] });
                }
              } else {
                onCreateCustom();
              }
            }}
          >
            <View style={styles.premiumBannerContent}>
              <Text style={styles.premiumBannerTitle}>
                Unlock Piclo Play
              </Text>
              <Text style={styles.premiumBannerText}>
                Get all current and future packs, plus the custom puzzle generator for $10.
              </Text>
            </View>
            <View style={styles.premiumBannerPrice}>
              <Text style={styles.premiumPriceText}>Get</Text>
            </View>
          </Pressable>
        </View>
      ) : (
        !hasDismissedCustomBanner && (
          <View>
            {/* The Custom Puzzle Shortcut Banner */}
            <Pressable
              style={({ pressed }) => [
                styles.customBanner,
                pressed && styles.customBannerPressed
              ]}
              onPress={onCreateCustom}
            >
              <View style={styles.customBannerContent}>
                <Text style={styles.customBannerTitle}>
                  Create a Custom Puzzle
                </Text>
                <Text style={styles.customBannerText}>
                  Turn your own images into playable Piclo puzzles.
                </Text>
              </View>
              <View style={[styles.customBannerAction, styles.actionButtonMarginRight]}>
                <Text style={styles.customActionText}>Go</Text>
              </View>

              <TouchableOpacity
                style={styles.dismissButton}
                onPress={handleDismissBanner}
                hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
              >
                <FontAwesome6 name="xmark" size={16} color="#c1c5ce" solid />
              </TouchableOpacity>
            </Pressable>
          </View>
        )
      )}

      <ScrollView
        showsVerticalScrollIndicator={false}
        style={styles.scrollView}
        contentContainerStyle={styles.scrollContent}
      >
        {mockPacks.map((pack) => {
          const isOwned = entitlements.picloPlay || entitlements.unlockedPacks.includes(pack.id);
          return (
            <PackCard key={pack.id} pack={pack} onSelect={onSelectPack} isOwned={isOwned} />
          );
        })}
      </ScrollView>
    </BaseModal>
  );
}

function PackCard({ pack, onSelect, isOwned }: { pack: PackData, onSelect: (id: string, title: string) => void, isOwned: boolean }) {
  const [isHovered, setIsHovered] = React.useState(false);

  const displayPrice = pack.priceLabel === 'Free' ? 'Free' : (isOwned ? 'Owned' : pack.priceLabel);

  return (
    <Pressable
      style={({ pressed }) => [
        styles.card,
        pack.isFeatured && styles.featuredCard,
        Platform.OS === 'web' && isHovered && styles.cardHover,
        pressed && styles.cardPressed
      ]}
      onPress={() => onSelect(pack.id, pack.title)}
      // @ts-ignore
      onHoverIn={() => setIsHovered(true)}
      // @ts-ignore
      onHoverOut={() => setIsHovered(false)}
    >
      <View style={styles.imageContainer}>
        <Image source={{ uri: pack.imageUri }} style={styles.cardImage} />
        {!isOwned && pack.priceLabel !== 'Free' && (
          <View style={styles.lockOverlay}>
            <FontAwesome6 name="lock" size={20} color="rgba(255,255,255,0.8)" solid />
          </View>
        )}
      </View>
      <View style={styles.cardBody}>
        <Text style={[styles.cardTitle, pack.isFeatured && styles.featuredTitle]}>{pack.title}</Text>
        <Text style={styles.cardDescription}>{pack.description}</Text>
        <View style={styles.cardMetaRow}>
          <Text style={styles.cardPrice}>{displayPrice}</Text>
          {pack.showChevron && (
            <FontAwesome6 name="chevron-right" size={14} color="#c1c5ce" solid />
          )}
        </View>
      </View>
    </Pressable>
  );
}

const styles = StyleSheet.create({
  headerRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 10,
    width: '100%',
  },
  backButton: {
    width: 44,
    height: 44,
    justifyContent: 'center',
    alignItems: 'center',
  },
  subtitleText: {
    ...typography.body,
    fontSize: 14,
    color: colors.textMuted,
    textAlign: 'center',
    marginBottom: 18,
  },
  scrollContent: {
    paddingHorizontal: 6, // Prevents shadow clipping
    paddingBottom: 20,
    gap: 12,
  },
  scrollView: {
    flex: 1,
  },
  card: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: colors.surface,
    borderRadius: 7,
    padding: 14,
    boxShadow: `2px 2px 0px ${colors.shadow}`,
    elevation: 3,
  },
  featuredCard: {
    backgroundColor: colors.background,
  },
  premiumBanner: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: '#fff', // Or a very faint terracotta tint like '#fdf7f5' if you want it to pop more
    borderWidth: 1,
    borderColor: colors.primary, // Using your Terracotta
    padding: 16,
    marginBottom: 20, // Creates a physical gap between the banner and the normal list
    boxShadow: `0px 4px 8px ${colors.primary}1A`,
    elevation: 4,
    borderRadius: 10,
    margin: 6,
  },
  premiumBannerPressed: {
    transform: [{ scale: 0.98 }],
    opacity: 0.9,
  },
  premiumBannerContent: {
    flex: 1,
    paddingRight: 16,
  },
  premiumBannerTitle: {
    ...typography.bodyBold,
    fontSize: 18,
    color: colors.text,
    marginBottom: 4,
  },
  premiumBannerText: {
    ...typography.body,
    color: colors.textMuted,
    lineHeight: 18,
  },
  premiumBannerPrice: {
    backgroundColor: colors.primary,
    paddingVertical: 8,
    paddingHorizontal: 14,
    borderRadius: 12,
  },
  premiumPriceText: {
    ...typography.bodyBold,
    color: '#fff',
    fontSize: 16,
  },
  customBanner: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: '#fff',
    borderWidth: 1,
    borderColor: colors.secondary,
    padding: 16,
    marginBottom: 20,
    boxShadow: `0px 4px 8px ${colors.secondary}1A`,
    elevation: 4,
    borderRadius: 10,
    margin: 6,
    position: 'relative',
  },
  customBannerPressed: {
    transform: [{ scale: 0.98 }],
    opacity: 0.9,
  },
  customBannerContent: {
    flex: 1,
    paddingRight: 16,
  },
  customBannerTitle: {
    ...typography.bodyBold,
    fontSize: 18,
    color: colors.text,
    marginBottom: 4,
  },
  customBannerText: {
    ...typography.body,
    color: colors.textMuted,
    lineHeight: 18,
  },
  customBannerAction: {
    backgroundColor: colors.secondary,
    paddingVertical: 8,
    paddingHorizontal: 14,
    borderRadius: 12,
  },
  actionButtonMarginRight: {
    marginRight: 16,
  },
  customActionText: {
    ...typography.bodyBold,
    color: '#fff',
    fontSize: 16,
  },
  dismissButton: {
    position: 'absolute',
    top: 8,
    right: 8,
    width: 24,
    height: 24,
    justifyContent: 'center',
    alignItems: 'center',
  },
  cardHover: {
    boxShadow: `4px 4px 0px ${colors.shadow}`,
    transform: [{ translateY: -1 }],
  },
  cardPressed: {
    opacity: 0.9,
    transform: [{ scale: 0.99 }],
  },
  imageContainer: {
    position: 'relative',
    marginRight: 14,
  },
  cardImage: {
    width: 68,
    height: 68,
    borderRadius: 14,
    backgroundColor: colors.background,
  },
  lockOverlay: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: 'rgba(0,0,0,0.3)',
    borderRadius: 14,
    justifyContent: 'center',
    alignItems: 'center',
  },
  cardBody: {
    flex: 1,
  },
  cardTitle: {
    ...typography.bodyBold,
    fontSize: 16,
    color: colors.text,
    marginBottom: 4,
  },
  featuredTitle: {
    color: colors.text,
  },
  cardDescription: {
    ...typography.body,
    fontSize: 13,
    color: colors.textMuted,
    marginBottom: 10,
    lineHeight: 18,
  },
  cardMetaRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  cardPrice: {
    ...typography.bodyBold,
    fontSize: 13,
    color: colors.text,
  },
});
